import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { HelpTip, type HelpContent } from './HelpTip';

/**
 * One job per element (§10.8): a label labels, a hint gives an example,
 * neither quietly does the other's job. The error replaces the hint rather
 * than stacking under it, so the field never grows two lines of small text.
 */
interface FieldShellProps {
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  /** An explanation one click away — what it is, how to write it, an example. */
  help?: HelpContent;
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string;
  action?: ReactNode;
}

export function Field({ label, hint, error, optional, help, children, className, action }: FieldShellProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const invalid = Boolean(error);

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1.5">
          <label htmlFor={id} className="text-ui font-medium text-strong">
            {label}
            {optional && <span className="ml-1.5 font-normal text-faint">— ixtiyoriy</span>}
          </label>
          {help && <HelpTip content={help} label={label} />}
        </span>
        {action}
      </div>
      {children({ id, describedBy: hint || error ? hintId : undefined, invalid })}
      {(error || hint) && (
        <p
          id={hintId}
          className={cn('text-ui', error ? 'text-accent-600 dark:text-accent-400' : 'text-muted')}
          // Errors are announced; hints are not, or every focus change would talk.
          {...(error ? { role: 'alert' } : {})}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

/**
 * The focus treatment is a border change AND a soft 3px ring, not a border
 * change alone. On a screen that is mostly form fields, that ring is most of
 * what people mean when they say an interface feels smooth: it gives the
 * transition somewhere to travel, so focus arrives instead of snapping.
 *
 * `focus:outline-none` is safe here only because the ring replaces it — the
 * global `:focus-visible` outline still covers everything else.
 */
const controlClass = (invalid: boolean) =>
  cn(
    'w-full rounded-control border bg-surface px-3 text-body text-strong',
    'placeholder:text-faint',
    'transition-ui duration-quick ease-out',
    'focus:outline-none',
    'disabled:cursor-not-allowed disabled:border-line disabled:bg-raised disabled:text-muted',
    invalid
      ? 'border-accent-500 focus:border-accent-500 focus:shadow-focus-danger'
      : 'border-line hover:border-line-strong focus:border-brand-500 focus:shadow-focus',
  );

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  function Input({ className, invalid = false, ...props }, ref) {
    return <input ref={ref} className={cn(controlClass(invalid), 'h-10', className)} {...props} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function Textarea({ className, invalid = false, ...props }, ref) {
  return <textarea ref={ref} className={cn(controlClass(invalid), 'min-h-[5.5rem] py-2.5 leading-relaxed', className)} {...props} />;
});

/** Slugs, URLs and IDs are machine strings and are set in mono on purpose:
 *  it makes a stray double space or a Cyrillic `с` visible (§10.1). */
export const MonoInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  function MonoInput({ className, invalid = false, ...props }, ref) {
    return (
      <input
        ref={ref}
        spellCheck={false}
        autoCapitalize="none"
        autoCorrect="off"
        className={cn(controlClass(invalid), 'h-10 font-mono text-ui tracking-tight', className)}
        {...props}
      />
    );
  },
);
